import React, { useMemo, useState } from 'react';
import { Operation } from '../../lib/types';
import { ConfluenceStats, planImpact, rankBySample, statsByCategory, statsByCombination } from '../../lib/confluence';
import { useJournal } from '../../context/JournalContext';
import { useDisplayMode } from '../../context/DisplayModeContext';
import { ConfluenceTable } from './ConfluenceTable';
import { cn } from '../../lib/cn';
import { fmtPctEs } from '../../lib/format';
const SAMPLE_OPTIONS = [5, 10, 20];
export function ConfluencePanel({
  ops
}: {
  ops: Operation[];
}) {
  const {
    state,
    categoriesSorted
  } = useJournal();
  const {
    formatResult
  } = useDisplayMode();
  const [minSample, setMinSample] = useState(10);
  const [customSample, setCustomSample] = useState('');
  const [side, setSide] = useState<'all' | 'buy' | 'sell'>('all');
  const [asset, setAsset] = useState('all');
  const [plan, setPlan] = useState<'all' | 'followed' | 'broken'>('all');
  const [outcome, setOutcome] = useState<'all' | 'pos' | 'neg'>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const assets = useMemo(() => Array.from(new Set(ops.map(op => op.asset))).sort((a, b) => a.localeCompare(b)), [ops]);
  const filtered = useMemo(() => ops.filter(op => {
    if (side !== 'all' && op.side !== side) return false;
    if (asset !== 'all' && op.asset !== asset) return false;
    if (plan === 'followed' && op.followedPlan !== true) return false;
    if (plan === 'broken' && op.followedPlan !== false) return false;
    if (outcome === 'pos' && op.resultPct <= 0) return false;
    if (outcome === 'neg' && op.resultPct >= 0) return false;
    if (from && op.date < from) return false;
    if (to && op.date > to) return false;
    return true;
  }), [ops, side, asset, plan, outcome, from, to]);
  const byCategory = useMemo(() => categoriesSorted.map(category => ({
    category,
    stats: statsByCategory(filtered, category, state.tags)
  })), [categoriesSorted, filtered, state.tags]);
  const allIndividual = byCategory.flatMap(group => group.stats);
  const individualRank = rankBySample(allIndividual, minSample);
  const combos = useMemo(() => statsByCombination(filtered, categoriesSorted, state.tags), [filtered, categoriesSorted, state.tags]);
  const comboRank = rankBySample(combos, minSample);
  const pairs = useMemo(() => {
    const result: {
      label: string;
      rows: ConfluenceStats[];
    }[] = [];
    for (let i = 0; i < categoriesSorted.length; i += 1) {
      for (let j = i + 1; j < categoriesSorted.length; j += 1) {
        const pairCategories = [categoriesSorted[i], categoriesSorted[j]];
        result.push({
          label: `${pairCategories[0].name} + ${pairCategories[1].name}`,
          rows: statsByCombination(filtered, pairCategories, state.tags)
        });
      }
    }
    return result;
  }, [categoriesSorted, filtered, state.tags]);
  const impact = useMemo(() => planImpact(filtered), [filtered]);
  if (ops.length === 0) {
    return <section className="mt-4 rounded-2xl border border-dashed border-line bg-surface/40 px-5 py-10 text-center" data-id="element-111" data-mp="bmiyxkg6y6xu">
        <h2 className="text-sm font-medium" data-id="element-112" data-mp="1qfvmb9ls29b">Rendimiento por confluencia</h2>
        <p className="mx-auto mt-1 max-w-md text-[13px] text-ink-soft" data-id="element-113" data-mp="1siw5p8dtdsa">
          Aún no hay datos suficientes para analizar las confluencias. Añade operaciones para empezar a detectar patrones.
        </p>
      </section>;
  }
  return <section className="mt-6" data-id="element-114" data-mp="pg0eyv9o51j4">
      <header className="mb-4" data-id="element-115" data-mp="pgtq08fnud72">
        <h2 className="text-[17px] font-semibold tracking-tight" data-id="element-116" data-mp="kpk33flhxnyv">Rendimiento por confluencia</h2>
        <p className="mt-0.5 text-[13px] text-ink-soft" data-id="element-117" data-mp="kn7eijl8gfcu">
          Calculado con tus categorías y etiquetas actuales por identificador, así que renombrarlas no pierde histórico.
        </p>
      </header>

      <div className="mb-5 flex flex-wrap items-end gap-2 rounded-2xl border border-line bg-white p-3" data-id="element-118" data-mp="18f62o45s657e">
        <Filter label="Desde" data-id="element-119" data-mp="sxeght90y8f3"><input type="date" value={from} onChange={event => setFrom(event.target.value)} className={inputClass} data-id="element-120" data-mp="swv936h032fb" /></Filter>
        <Filter label="Hasta" data-id="element-121" data-mp="xx0tc1kxxmnd"><input type="date" value={to} onChange={event => setTo(event.target.value)} className={inputClass} data-id="element-122" data-mp="xxiw7h23yz49" /></Filter>
        <Filter label="Activo" data-id="element-123" data-mp="ny1utbtw37e3">
          <select value={asset} onChange={event => setAsset(event.target.value)} className={inputClass} data-id="element-124" data-mp="nx9zm247fboc">
            <option value="all" data-id="element-125" data-mp="1bonqe3mpiz77">Todos</option>
            {assets.map(item => <option key={item} value={item} data-id="element-126" data-mp="1gmfurx4jgdsa">{item}</option>)}
          </select>
        </Filter>
        <Filter label="Compra / Venta" data-id="element-127" data-mp="12vassodp79es">
          <select value={side} onChange={event => setSide(event.target.value as typeof side)} className={inputClass} data-id="element-128" data-mp="12uj5e0u98hzl">
            <option value="all" data-id="element-129" data-mp="16v32j4etvllq">Todas</option>
            <option value="buy" data-id="element-130" data-mp="1umqiirajxc98">Compra</option>
            <option value="sell" data-id="element-131" data-mp="1ukxg5o6z1e71">Venta</option>
          </select>
        </Filter>
        <Filter label="Resultado" data-id="element-132" data-mp="1ze8jvgghleuq">
          <select value={outcome} onChange={event => setOutcome(event.target.value as typeof outcome)} className={inputClass} data-id="element-133" data-mp="1zfbdvx93vxev">
            <option value="all" data-id="element-134" data-mp="3f4so0ps3nsyo">Todos</option>
            <option value="pos" data-id="element-135" data-mp="3f3zr0giy0htq">Positivos</option>
            <option value="neg" data-id="element-136" data-mp="3tt5o9yy7zxyz">Negativos</option>
          </select>
        </Filter>
        <Filter label="Seguí el plan" data-id="element-137" data-mp="3oucae5f0u4ya">
          <select value={plan} onChange={event => setPlan(event.target.value as typeof plan)} className={inputClass} data-id="element-138" data-mp="3ovdmmd7rwxhl">
            <option value="all" data-id="element-139" data-mp="3159ub201ck5w">Todas</option>
            <option value="followed" data-id="element-140" data-mp="2w5wxbofg9wpl">Solo siguiendo el plan</option>
            <option value="broken" data-id="element-141" data-mp="2w7huzmx29vro">Solo fuera del plan</option>
          </select>
        </Filter>
        <Filter label="Muestra mínima" data-id="element-142" data-mp="3b48vjzih1div">
          <div className="flex items-center gap-1" data-id="element-143" data-mp="3644eo8dnigs4">
            {SAMPLE_OPTIONS.map(option => <button key={option} type="button" onClick={() => {
            setMinSample(option);
            setCustomSample('');
          }} className={cn('h-9 rounded-lg border px-2.5 text-[12px] font-medium', minSample === option && !customSample ? 'border-transparent bg-ink text-white' : 'border-line bg-white text-ink-soft hover:text-ink')} data-id="element-144" data-mp="qc713fzhftga">
                {option}
              </button>)}
            <input type="number" min={1} placeholder="Pers." value={customSample} onChange={event => {
            setCustomSample(event.target.value);
            const parsed = parseInt(event.target.value, 10);
            if (!Number.isNaN(parsed) && parsed > 0) setMinSample(parsed);
          }} className={cn(inputClass, 'w-[72px]')} data-id="element-145" data-mp="12cwxd3jfgkg2" />
          </div>
        </Filter>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3" data-id="element-146" data-mp="ogjgnwmpwn2n">
        {byCategory.map(({
        category,
        stats
      }) => {
        const ranked = rankBySample(stats, minSample);
        const best = ranked.best[0];
        const worst = ranked.worst[0] && ranked.worst[0].key !== best?.key ? ranked.worst[0] : undefined;
        return <div key={category.id} className="rounded-2xl border border-line bg-white p-4 shadow-card" data-id="element-147" data-mp="257vf4ulw9wvp">
              <p className="text-[12px] font-medium text-ink-soft" data-id="element-148" data-mp="207zjpjr9jdrr">Mejor {category.name}</p>
              {best ? <>
                  <p className="mt-1 text-[17px] font-semibold tracking-tight" data-id="element-149" data-mp="1v8wd3uvmwzho">{best.label}</p>
                  <p className={cn('mt-0.5 text-xl font-semibold tabular-nums', best.resultPct >= 0 ? 'text-pos' : 'text-neg')} data-id="element-150" data-mp="1qb4k0xj977bw">
                    {formatResult({
                pct: best.resultPct,
                money: best.resultMoney
              })}
                  </p>
                  <p className="mt-1 text-[12px] text-ink-soft" data-id="element-151" data-mp="1hfn6u32zgu3d">
                    {best.winRate.toFixed(2)} % Win Rate · {best.count} operaciones · Fiabilidad: {best.reliability}
                  </p>
                </> : <p className="mt-2 text-[13px] text-ink-soft" data-id="element-152" data-mp="12kh3xoz6pbe9">Muestra insuficiente</p>}

              <div className="mt-3 border-t border-line pt-3" data-id="element-153" data-mp="2rfzh7ancmehd">
                <p className="text-[12px] font-medium text-ink-soft" data-id="element-154" data-mp="2vgjau48pqzyr">Peor {category.name}</p>
                {worst ? <>
                    <p className="mt-1 text-[14px] font-semibold tracking-tight" data-id="element-155" data-mp="2hfystaamh86b">{worst.label}</p>
                    <p className={cn('mt-0.5 text-[15px] font-semibold tabular-nums', worst.resultPct >= 0 ? 'text-pos' : 'text-neg')} data-id="element-156" data-mp="2mejvdgdck3xx">
                      {formatResult({
                  pct: worst.resultPct,
                  money: worst.resultMoney
                })}
                    </p>
                    <p className="mt-0.5 text-[11px] text-ink-soft" data-id="element-157" data-mp="1yq90quy8v0i3">
                      {worst.winRate.toFixed(2)} % Win Rate · {worst.count} operaciones · Fiabilidad: {worst.reliability}
                    </p>
                  </> : <p className="mt-1 text-[12.5px] text-ink-soft" data-id="element-158" data-mp="1ote5qm16ji8k">Muestra insuficiente</p>}
              </div>
            </div>;
      })}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-159" data-mp="1a4m79anss1lb">
        <RankCard title="Mejores confluencias" rows={individualRank.best.slice(0, 5)} tone="pos" insufficient={individualRank.insufficient.length} data-id="element-160" data-mp="163siyg6g0krw" />
        <RankCard title="Peores confluencias" rows={individualRank.worst.slice(0, 5)} tone="neg" insufficient={individualRank.insufficient.length} data-id="element-161" data-mp="1k4czf5ovy4i8" />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-162" data-mp="1t404q2kqjugk">
        <ComboHighlight title="Mejor configuración completa" stats={comboRank.best[0]} data-id="element-163" data-mp="1t25fic2flg6o" />
        <ComboHighlight title="Peor configuración completa" stats={comboRank.worst[0]} data-id="element-164" data-mp="1p2ekfw5vgdwc" />
      </div>

      <div className="mt-6 space-y-5" data-id="element-165" data-mp="22t9u3ao72z0z">
        {byCategory.map(({
        category,
        stats
      }) => <div key={category.id} data-id="element-166" data-mp="1xvqikiivt0cq">
            <h3 className="mb-2 text-[15px] font-semibold tracking-tight" data-id="element-167" data-mp="2cs8wwcma40g2">Rendimiento por {category.name}</h3>
            {stats.length === 0 ? <p className="rounded-xl border border-dashed border-line bg-surface/40 px-4 py-5 text-center text-[13px] text-ink-soft" data-id="element-168" data-mp="27ufnzbtlcsc3">
                Aún no hay operaciones etiquetadas en {category.name}.
              </p> : <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-169" data-mp="25yuvnm6i65f7">
                {stats.map(item => <div key={item.key} className="rounded-2xl border border-line bg-white p-4" data-id="element-170" data-mp="21xjdu2tveohk">
                    <div className="flex items-start justify-between gap-3" data-id="element-171" data-mp="2yna8q362qdnw">
                      <div className="min-w-0" data-id="element-172" data-mp="2ylfv79x4tntu">
                        <p className="truncate text-[13px] font-semibold tracking-tight" data-id="element-173" data-mp="2to7b33sfb9py">{item.label}</p>
                        <p className="mt-0.5 text-[11px] text-ink-soft" data-id="element-174" data-mp="2plswwwzp8y50">
                          {item.count} operaciones · {item.wins}G · {item.losses}P · Fiabilidad: {item.reliability}
                        </p>
                      </div>
                      <span className={cn('shrink-0 text-[15px] font-semibold tabular-nums', item.resultPct >= 0 ? 'text-pos' : 'text-neg')} data-id="element-175" data-mp="1e5cbmn1grna8">
                        {formatResult({
                  pct: item.resultPct,
                  money: item.resultMoney
                })}
                      </span>
                    </div>
                    <dl className="mt-3 grid grid-cols-2 gap-2 text-[12px] sm:grid-cols-3" data-id="element-176" data-mp="3pvadxo547yue">
                      <Detail label="Win Rate" value={`${item.winRate.toFixed(2)} %`} data-id="element-177" data-mp="3tvcic528j6dd" />
                      <Detail label="Resultado medio" value={fmtPctEs(item.avgPct)} data-id="element-178" data-mp="2irjbj06rjtb" />
                      <Detail label="RR medio" value={item.avgR.toFixed(2)} data-id="element-179" data-mp="7fzw142kevn6" />
                      <Detail label="Profit Factor" value={item.profitFactor === null ? '—' : item.profitFactor.toFixed(2)} data-id="element-180" data-mp="cfmhg0sjri72" />
                      <Detail label="Mejor operación" value={fmtPctEs(item.bestPct)} tone="pos" data-id="element-181" data-mp="hgk0sgpamia5" />
                      <Detail label="Peor operación" value={fmtPctEs(item.worstPct)} tone="neg" data-id="element-182" data-mp="lfj1tq7wluwb" />
                      <Detail label="Cumplimiento del plan" value={`${item.planFollowRate.toFixed(0)} %`} data-id="element-183" data-mp="2j7x3cz3sge31" />
                      <Detail label="Drawdown" value={fmtPctEs(-item.maxDrawdown, false)} data-id="element-184" data-mp="2n8yvyx379rw1" />
                      <Detail label="Puntuación" value={item.count >= minSample ? item.score.toFixed(1) : 'Muestra insuficiente'} data-id="element-185" data-mp="3qjw44ndpvdnk" />
                    </dl>
                  </div>)}
              </div>}
          </div>)}
      </div>

      <div className="mt-6" data-id="element-186" data-mp="3vifv52763fms">
        <h3 className="mb-3 text-[15px] font-semibold tracking-tight" data-id="element-187" data-mp="3vhegknqy0b0b">Combinaciones DZE</h3>
        <ConfluenceTable rows={combos} minSample={minSample} data-id="element-188" data-mp="dbjb7ewfpd3a" />
        {combos.length > 0 && <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-189" data-mp="8cgh2phzsra3">
            {combos.slice(0, 4).map(combo => <div key={combo.key} className="rounded-2xl border border-line bg-white p-4" data-id="element-190" data-mp="n1use354pis4">
                <p className="text-[13px] font-semibold tracking-tight" data-id="element-191" data-mp="i4m9u1g2jcqc">{combo.label}</p>
                <p className="mt-0.5 text-[11px] text-ink-soft" data-id="element-192" data-mp="w43xgdhmojy4">{combo.count} operaciones · Fiabilidad: {combo.reliability}</p>
                <dl className="mt-3 grid grid-cols-2 gap-2 text-[12px]" data-id="element-193" data-mp="r5spy9et2lj2">
                  <Detail label="Win Rate" value={`${combo.winRate.toFixed(1)} %`} data-id="element-194" data-mp="1ar2que01pofh" />
                  <Detail label="Resultado" value={formatResult({
              pct: combo.resultPct,
              money: combo.resultMoney
            })} tone={combo.resultPct >= 0 ? 'pos' : 'neg'} data-id="element-195" data-mp="1fp458lmj0zkm" />
                  <Detail label="Ganancia media" value={fmtPctEs(combo.avgWinPct)} data-id="element-196" data-mp="15qoguqevw4et" />
                  <Detail label="Pérdida media" value={fmtPctEs(combo.avgLossPct)} data-id="element-197" data-mp="15srd3s6mqtrz" />
                  <Detail label="Mejor operación" value={fmtPctEs(combo.bestPct)} data-id="element-198" data-mp="rxyuulzcuzhp" />
                  <Detail label="Peor operación" value={fmtPctEs(combo.worstPct)} data-id="element-199" data-mp="wqrvaxnsjlnz" />
                  <Detail label="Racha ganadora" value={String(combo.winStreak)} data-id="element-200" data-mp="i256848d3fjk" />
                  <Detail label="Racha perdedora" value={String(combo.lossStreak)} data-id="element-201" data-mp="mzngbxy480t5" />
                  <Detail label="RR medio" value={combo.avgR.toFixed(2)} data-id="element-202" data-mp="90nwd3ljsa3b" />
                  <Detail label="Profit Factor" value={combo.profitFactor === null ? '—' : combo.profitFactor.toFixed(2)} data-id="element-203" data-mp="92rwdagbsoyb" />
                  <Detail label="Cumplimiento del plan" value={`${combo.planFollowRate.toFixed(0)} %`} data-id="element-204" data-mp="5yjcetghe1vv" />
                  <Detail label="Drawdown" value={fmtPctEs(-combo.maxDrawdown, false)} data-id="element-205" data-mp="z7tpewk0mbn" />
                </dl>
              </div>)}
          </div>}
      </div>

      <div className="mt-6" data-id="element-206" data-mp="3ocsaajk1f898">
        <h3 className="mb-3 text-[15px] font-semibold tracking-tight" data-id="element-207" data-mp="3odlkjui7gfw4">Análisis por pares</h3>
        <div className="grid grid-cols-1 gap-4" data-id="element-208" data-mp="3je8nyi1j7ugc">
          {pairs.map(pair => <div key={pair.label} data-id="element-209" data-mp="3eew0pw6r0ld8">
              <p className="mb-2 text-[12px] font-medium text-ink-soft" data-id="element-210" data-mp="3edl0u7gfbxre">{pair.label}</p>
              <ConfluenceTable rows={pair.rows} minSample={minSample} data-id="element-211" data-mp="39erle9hktwol" />
            </div>)}
        </div>
      </div>

      <div className="mt-6" data-id="element-212" data-mp="35fir93imjwsi">
        <h3 className="mb-3 text-[15px] font-semibold tracking-tight" data-id="element-213" data-mp="35ganmgk1pyju">Impacto de seguir el plan</h3>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-214" data-mp="17ltw2nn0dm8e">
          {[impact.followed, impact.broken].map(stats => <div key={stats.key} className="rounded-2xl border border-line bg-white p-4" data-id="element-215" data-mp="2lkpv4u4d58od">
              <p className="text-[13px] font-semibold tracking-tight" data-id="element-216" data-mp="2qj1472mu3f5j">{stats.label}</p>
              <p className="mt-0.5 text-[11px] text-ink-soft" data-id="element-217" data-mp="2v9zk413wub89">{stats.count} operaciones · Fiabilidad: {stats.reliability}</p>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-[12px]" data-id="element-218" data-mp="2zbulf5lucom1">
                <Detail label="Win Rate" value={`${stats.winRate.toFixed(1)} %`} data-id="element-219" data-mp="22lbw3xrs17r0" />
                <Detail label="Resultado" value={formatResult({
              pct: stats.resultPct,
              money: stats.resultMoney
            })} tone={stats.resultPct >= 0 ? 'pos' : 'neg'} data-id="element-220" data-mp="22nerkxbp7t34" />
                <Detail label="RR medio" value={stats.avgR.toFixed(2)} data-id="element-221" data-mp="2bl8p2bl78srv" />
                <Detail label="Profit Factor" value={stats.profitFactor === null ? '—' : stats.profitFactor.toFixed(2)} data-id="element-222" data-mp="2glmo544tosdr" />
                <Detail label="Drawdown" value={fmtPctEs(-stats.maxDrawdown, false)} data-id="element-223" data-mp="3n05idxof2451" />
                <Detail label="Media por operación" value={fmtPctEs(stats.avgPct)} data-id="element-224" data-mp="3rzgyr5kvf7m3" />
              </dl>
            </div>)}
        </div>
        {impact.unknown > 0 && <p className="mt-2 text-[12px] text-ink-soft" data-id="element-225" data-mp="1nwp4c8ydh1az">
            {impact.unknown} {impact.unknown === 1 ? 'operación sin indicar' : 'operaciones sin indicar'} si se siguió el plan.
          </p>}
      </div>
    </section>;
}
const inputClass = 'h-9 rounded-lg border border-line bg-white px-2.5 text-[12.5px] text-ink focus:outline-none focus:ring-2 focus:ring-accent/25';
function Filter({
  label,
  children
}: {
  label: string;
  children: React.ReactNode;
}) {
  return <label className="flex flex-col gap-1" data-id="element-226" data-mp="1ixc8iypiwjw9">
      <span className="text-[11px] font-medium text-ink-soft" data-id="element-227" data-mp="1iyduhy82uqsr">{label}</span>
      {children}
    </label>;
}
function RankCard({
  title,
  rows,
  tone,
  insufficient
}: {
  title: string;
  rows: ConfluenceStats[];
  tone: 'pos' | 'neg';
  insufficient: number;
}) {
  const {
    formatResult
  } = useDisplayMode();
  return <div className="rounded-2xl border border-line bg-white p-4" data-id="element-228" data-mp="lh5ff14f9obo">
      <p className="text-[13px] font-semibold tracking-tight" data-id="element-229" data-mp="2x4s34t1frd03">{title}</p>
      {rows.length === 0 ? <p className="mt-2 text-[13px] text-ink-soft" data-id="element-230" data-mp="31wa5nlj3xln6">Muestra insuficiente</p> : <ul className="mt-3 space-y-2" data-id="element-231" data-mp="2o55e76pw7znx">
          {rows.map(row => <li key={row.key} className="flex items-center justify-between gap-3" data-id="element-232" data-mp="2o2smw2y8wcy7">
              <div className="min-w-0" data-id="element-233" data-mp="2s44euf4lda9k">
                <p className="truncate text-[13px] font-medium" data-id="element-234" data-mp="3fv9vgwicw6kx">{row.label}</p>
                <p className="text-[11px] text-ink-soft" data-id="element-235" data-mp="3kuw6czappaqk">{row.count} operaciones · {row.winRate.toFixed(1)} % WR · Fiabilidad: {row.reliability}</p>
              </div>
              <span className={cn('shrink-0 text-[13px] font-semibold tabular-nums', tone === 'pos' ? 'text-pos' : 'text-neg')} data-id="element-236" data-mp="36utsrp9ft3h4">
                {formatResult({
            pct: row.resultPct,
            money: row.resultMoney
          })}
              </span>
            </li>)}
        </ul>}
      {insufficient > 0 && <p className="mt-3 text-[11px] text-ink-soft" data-id="element-237" data-mp="20gcb8qausrjt">{insufficient} etiquetas sin muestra suficiente para clasificar.</p>}
    </div>;
}
function ComboHighlight({
  title,
  stats
}: {
  title: string;
  stats?: ConfluenceStats;
}) {
  const {
    formatResult
  } = useDisplayMode();
  return <div className="rounded-2xl border border-line bg-white p-4" data-id="element-238" data-mp="2i4y879bdl30l">
      <p className="text-[12px] font-medium text-ink-soft" data-id="element-239" data-mp="2d64ylrz7lw5p">{title}</p>
      {stats ? <>
          <p className="mt-1 text-[15px] font-semibold tracking-tight" data-id="element-240" data-mp="3atdff9o9mh1d">{stats.label}</p>
          <p className="mt-1 text-[12px] text-ink-soft" data-id="element-241" data-mp="35tha973u0jk8">
            {stats.count} operaciones · {stats.winRate.toFixed(2)} % Win Rate · Fiabilidad: {stats.reliability}
          </p>
          <p className={cn('mt-1 text-xl font-semibold tabular-nums', stats.resultPct >= 0 ? 'text-pos' : 'text-neg')} data-id="element-242" data-mp="2w4h5t86k8vyn">
            {formatResult({
          pct: stats.resultPct,
          money: stats.resultMoney
        })}
          </p>
        </> : <p className="mt-2 text-[13px] text-ink-soft" data-id="element-243" data-mp="3ortqtdx2ovpu">Muestra insuficiente para clasificar esta configuración.</p>}
    </div>;
}
function Detail({
  label,
  value,
  tone = 'neutral'
}: {
  label: string;
  value: string;
  tone?: 'neutral' | 'pos' | 'neg';
}) {
  return <div className="rounded-lg bg-surface/55 px-2.5 py-2" data-id="element-244" data-mp="9x2z2dvb7fg4">
      <dt className="text-[10px] font-medium text-ink-soft" data-id="element-245" data-mp="9yw4xqy3lvn2">{label}</dt>
      <dd className={cn('mt-0.5 truncate font-medium tabular-nums', tone === 'pos' && 'text-pos', tone === 'neg' && 'text-neg')} data-id="element-246" data-mp="ewnwukyqthr9">{value}</dd>
    </div>;
}